<!-- 
Dashboard widget showing a graph of system activity.

This widget has two modes: one showing activity for the last week and the other showing activity for the last year.
-->
<template>
  <v-flex xs12 md6 lg4 xl3>
    <v-card flat class="home">
      <our-widget-toolbar :title="title" @refresh="forceReload" @remove="$emit('removeWidget')" @toggleChartType="$emit('toggle')" />
      <our-busy-indicator :show="loading"/>
      <v-card-text class="pb-3">
        <line-chart 
          v-if="!isChartEmpty(chartData)" ref="chart" :chart-data="chartData" :chart-options="chartOptions" 
          style="height: 265px"
        />
        <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
      </v-card-text>
    </v-card>
  </v-flex>
</template>

<script>
import _ from "underscore";
import moment from "moment";
import LineChart from "@/components/common/chart/LineChart.vue";
import lookupMixins from "@/mixins/lookup.mixins";
import * as dashboardAction from "@/store/dashboard/action-types";
import * as action from "@/store/system-logs/action-types";
import * as mutation from "@/store/system-logs/mutation-types";
import chartMixins from "@/mixins/chart.mixins";

export default {
  components: { LineChart },
  mixins: [lookupMixins, chartMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    toggled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: this.chartClick,
        onHover: this.setChartHoverCursor,
        tooltips: {
          callbacks: {
            label: this.getTooltipLabel,
            title: this.getTooltipTitle,
            afterBody: this.getTooltipFooter
          }
        },
        scales: {
          xAxes: [{ stacked: true }],
          yAxes: [{ stacked: true }]
        },
        legend: { display: false }
      };
    },
    state() {
      return this.toggled ? this.$store.state.dashboard.activityForYear : this.$store.state.dashboard.activityForWeek;
    },
    loading() {
      return this.state.loading;
    },
    loaded() {
      return this.state.loaded;
    },
    chartData() {
      if (!this.loaded) return { labels: [""], datasets: [] };
      const format = this.toggled ? "MMM" : "ddd";
      const labels = _(_(this.state.data).pluck("date")).map(d => moment(d).format(format));
      const counts = _(this.state.data).pluck("count");
      const datasets = [
        {
          data: counts,
          fill: "origin",
          backgroundColor: this.toggled ? "#C8E6C9" : "#BBDEFB",
          borderColor: this.toggled ? "#4CAF50" : "#2196F3"
        }
      ];
      return { labels, datasets };
    },
    title() {
      return this.toggled ? "Activity for year" : "Activity for week";
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    },
    toggled() {
      this.drawChart();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    forceReload() {
      const action = this.toggled ? dashboardAction.ACTIVITY_FOR_YEAR_GET : dashboardAction.ACTIVITY_FOR_WEEK_GET;
      this.$store.dispatch(action, this.levyCollectionId);
    },
    refresh() {
      const action = this.toggled
        ? dashboardAction.ACTIVITY_FOR_YEAR_REFRESH
        : dashboardAction.ACTIVITY_FOR_WEEK_REFRESH;
      this.$store.dispatch(action, this.levyCollectionId);
      if (this.loaded) {
        this.drawChart();
      }
    },
    drawChart() {
      this.$nextTick(() => {
        if (this.$refs.chart) this.$refs.chart.display();
      });
    },
    chartClick(e) {
      if (this.getActivityCount(e) > 0) {
        const index = this.getHoverIndex(e);
        const fromDate = moment(this.state.data[index].date);
        const toDate = moment(this.state.data[index].date);
        this.toggled ? toDate.add(1, "months").subtract(1, "days") : toDate;
        const criteria = { fromDate: fromDate.format("YYYY-MM-DD"), toDate: toDate.format("YYYY-MM-DD") };
        this.$store.commit(mutation.LIST_CLEAR_CRITERIA);
        this.$store.commit(mutation.LIST_SET_FILTERS, { levyCollectionId: this.levyCollectionId });
        this.$store.commit(mutation.LIST_SET_CRITERIA, criteria);
        this.$store.commit(mutation.LIST_KEEP_CRITERIA, true);
        this.$store.dispatch(action.LIST);
        this.$router.push(`/levy-collections/${this.levyCollectionId}/history`);
      }
    },
    setChartHoverCursor(e) {
      const chartObject = this.$refs.chart.$data._chart;
      chartObject.canvas.style.cursor = this.getActivityCount(e) > 0 ? "pointer" : "default";
    },
    getActivityCount(e) {
      return this.getActivityCountForIndex(this.getHoverIndex(e));
    },
    getActivityCountForIndex(index) {
      return _(this.state.data[index].count);
    },
    getHoverIndex(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = chartObject.getElementsAtXAxis(e);
      return activePoints[0]["_index"];
    },
    getTooltipLabel(tooltipItem, data) {
      if (!data || !data.datasets) return null;
      const number = data.datasets[0].data[tooltipItem.index];
      return number > 0 ? `${number} logged activities` : null;
    },
    getTooltipTitle(tooltipItems) {
      const index = _(tooltipItems).first().index;
      const date = moment(this.state.data[index].date);
      const format = this.toggled ? "MMMM YYYY" : "dddd Do MMMM";
      return date.format(format);
    },
    getTooltipFooter(tooltipItems) {
      const index = _(tooltipItems).first().index;
      return this.getActivityCountForIndex(index) > 0 ? ["", "(click to show)"] : null;
    }
  }
};
</script>
